import { Box, Typography } from '@mui/material';
import { SwipeableItem } from 'components/commons';
import {
  AddTagDialog,
  EditTagDialog,
  SearchTagDialog,
} from 'components/features/Tag';
import { useHeaderContext } from 'components/providers/HeaderProvider';
import i18next from 'i18next';
import { useEffect, useState } from 'react';
import { TABLE_PAGE_INIT } from 'utils/constant';
import { commonErrorMessage } from 'utils/error-message';
import ja from 'utils/ja';
import {
  addTagLS,
  deleteTagLS,
  getTagsLS,
  ITag,
  updateTagLS,
} from 'utils/localTag';

const Tag = () => {
  const [tags, setTags] = useState<ITag[]>([]);
  const [tagNameVaLue, setTagNameValue] = useState<string>('');
  const [openDialogSearch, setOpenDialogSearch] = useState<boolean>(false);
  const [openDialogAdd, setOpenDialogAdd] = useState<boolean>(false);
  const { stateClick, setStateClick } = useHeaderContext();
  const [tagSelected, setTagSelected] = useState<ITag | null>(null);
  const [query, setQuery] = useState<{ page: string; tagName: string }>({
    tagName: '',
    page: TABLE_PAGE_INIT,
  });
  const [openDialogEdit, setOpenDialogEdit] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const handleConfirmDelete = async () => {
    try {
      if (tagSelected) {
        deleteTagLS(tagSelected.id);
        handleGetListTags();
      }
      handleGetListTags();
    } catch (error) {
      //empty
    }
  };

  const handleConfirmEdit = async () => {
    if (tagSelected) {
      const trimmedValue = tagSelected.tagName.trim();

      if (!trimmedValue) {
        setErrorMessage(commonErrorMessage.MSE00003(ja.tag.tags));
        return;
      }

      const isDuplicate = tags.some(
        (tag) => tag.tagName.trim() === tagSelected.tagName.trim()
      );

      if (isDuplicate) {
        setErrorMessage(commonErrorMessage.TAG_DUPLICATE);
        return;
      }

      try {
        if (tagSelected) {
          updateTagLS(tagSelected.id, tagSelected.tagName);
          setOpenDialogEdit(false);
          handleGetListTags();
        }
      } catch (error) {
        /* empty */
      }
    }
  };

  const handleConfirmAdd = async () => {
    try {
      const trimmedValue = tagNameVaLue.trim();

      if (!trimmedValue) {
        setErrorMessage(commonErrorMessage.MSE00003(ja.tag.tags));
        return;
      }

      const isDuplicate = tags.some(
        (tag) => tag.tagName.trim() === tagNameVaLue.trim()
      );

      if (isDuplicate) {
        setErrorMessage(commonErrorMessage.TAG_DUPLICATE);
        return;
      }

      const newTag: ITag = {
        id: Date.now().toString(),
        tagName: tagNameVaLue.trim(),
      };
      addTagLS(newTag);
      setTagNameValue('');
      setErrorMessage('');
      setOpenDialogAdd(false);
      handleGetListTags();
    } catch (error) {
      /* empty */
    }
  };

  const handleGetListTags = async () => {
    try {
      let data = getTagsLS();
      if (query.tagName) {
        data = data.filter((t) =>
          t.tagName.toLowerCase().includes(query.tagName.toLowerCase())
        );
      }
      setTags(data);
    } catch (error) {
      /* empty */
    }
  };

  const handleConfirmSearch = () => {
    handleGetListTags();
    setOpenDialogSearch(false);
    setStateClick({ click: false, type: '' });
  };

  useEffect(() => {
    handleGetListTags();
  }, []);

  useEffect(() => {
    if (stateClick?.click && stateClick.type === 'search') {
      setOpenDialogSearch(true);
    }
    if (stateClick?.click && stateClick.type === 'add') {
      setOpenDialogAdd(true);
    }
  }, [stateClick]);

  return (
    <Box
      sx={{
        overflowY: 'auto',
        padding: '10px',
        height: { xs: 'calc(100dvh - 60px)', sm: 'calc(100dvh - 80px)' },
        mt: { xs: '60px', sm: '80px' },
      }}
    >
      {!tags?.length && (
        <Typography
          component='p'
          sx={{ fontSize: '36px', textAlign: 'center', mt: '20%' }}
        >
          {ja.common.not_found}
        </Typography>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          gap: '10px',
        }}
      >
        {tags.map((tag) => (
          <SwipeableItem
            key={tag.id}
            handleDelete={handleConfirmDelete}
            handleSelected={() => setTagSelected(tag)}
            confirmDeleteTitle={ja.tag.dialog_delete.title}
            confirmDeleteContent={i18next.t(ja.tag.dialog_delete.content, {
              0: tag.tagName,
            })}
          >
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
              onClick={() => {
                setTagSelected(tag);
                setOpenDialogEdit(true);
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: '20px', sm: '35px' },
                  color: '#000',
                  fontWeight: 'bold',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: '1',
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {tag.tagName}
              </Typography>
            </Box>
          </SwipeableItem>
        ))}
      </Box>
      <EditTagDialog
        open={openDialogEdit}
        onClose={() => {
          setOpenDialogEdit(false);
          setErrorMessage('');
        }}
        handleConfirm={handleConfirmEdit}
        value={tagSelected?.tagName ?? ''}
        setValue={(val) => {
          setTagSelected({
            id: tagSelected?.id ?? '',
            tagName: val,
          });
          if (errorMessage) setErrorMessage('');
        }}
        errorMessage={errorMessage}
      />
      <SearchTagDialog
        open={openDialogSearch}
        onClose={() => {
          setOpenDialogSearch(false);
          setStateClick({ click: false, type: '' });
        }}
        value={query.tagName}
        setValue={(val) =>
          setQuery((oldState) => ({ ...oldState, tagName: val }))
        }
        handleConfirm={handleConfirmSearch}
      />
      <AddTagDialog
        open={openDialogAdd}
        onClose={() => {
          setOpenDialogAdd(false);
          setStateClick({ click: false, type: '' });
          setErrorMessage('');
        }}
        handleConfirm={() => handleConfirmAdd()}
        value={tagNameVaLue}
        setValue={(v) => {
          setTagNameValue(v);
          if (errorMessage) setErrorMessage('');
        }}
        errorMessage={errorMessage}
      />
    </Box>
  );
};

export default Tag;
